import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
    const { user, loading, isAuthenticated } = useAuth();
    const location = useLocation();

    // Debug: Log every render
    console.log('ProtectedRoute render:', {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state,
        requireAdmin,
        userRole: user?.role,
        isAuthenticated,
        loading
    });

    // Show loading spinner while checking authentication
    if (loading) {
        console.log('ProtectedRoute: Loading state at path:', location.pathname);
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        console.log('ProtectedRoute: Not authenticated, redirecting to login from:', location.pathname);
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check admin requirement
    if (requireAdmin && user?.role !== 'admin') {
        console.log('ProtectedRoute: Admin check failed', {
            path: location.pathname,
            requireAdmin,
            userRole: user?.role,
            isAuthenticated
        });
        return <Navigate to="/dashboard" replace />;
    }

    console.log('ProtectedRoute: Rendering content for:', location.pathname);
    return <>{children}</>;
};

export default ProtectedRoute;
